import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { canAct } from "@/lib/access";
import { useRealtimeTick } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet, FileText, Download, Megaphone } from "lucide-react";
import { fmtNum, MONTH_NAMES } from "@/lib/helpers";

function TargetProgressCard({ tp, role }) {
  if (!tp) return null;
  const own = role === "personel" ? "Benim Hedefim" : role === "mudur" ? "Şubem" : "Firma";
  const rows = [[own, tp.company], ...tp.branches.map((b) => [`Şube · ${b.name}`, b]), ...tp.employees.map((e) => [e.name, e])]
    .filter(([, r]) => r && r.total_target > 0);
  if (rows.length === 0) return null;
  return (
    <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-200" data-testid="report-target-progress">
      <CardHeader><CardTitle className="text-base">Hedef Gerçekleşme (Adet)</CardTitle></CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800">
              <TableHead>Kapsam</TableHead><TableHead className="text-right">Hedef</TableHead><TableHead className="text-right">Satılan</TableHead>
              <TableHead className="text-right">Mevcut HGO</TableHead><TableHead className="text-right">Ay Sonu Tahmin</TableHead><TableHead className="text-right">Tahmini HGO</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([label, r]) => (
              <TableRow key={label} className="border-slate-800">
                <TableCell className="font-semibold">{label}</TableCell>
                <TableCell className="text-right">{fmtNum(r.total_target)}</TableCell>
                <TableCell className="text-right text-emerald-300">{fmtNum(r.total_sold)}</TableCell>
                <TableCell className="text-right font-bold">%{r.hgo}</TableCell>
                <TableCell className="text-right">{fmtNum(r.forecast_qty)}</TableCell>
                <TableCell className="text-right font-bold text-blue-300">%{r.forecast_hgo}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);

  const tick = useRealtimeTick();
  useEffect(() => {
    http.get(`/reports/monthly?year=${year}&month=${month}`).then((r) => setReport(r.data));
  }, [year, month, tick]);

  const download = async (fmt) => {
    const res = await http.get(`/reports/export/${fmt}?year=${year}&month=${month}`, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `rapor_${year}_${String(month).padStart(2, "0")}.${fmt === "excel" ? "xlsx" : "pdf"}`;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  if (!report) return <div className="text-slate-400">Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Ay Sonu Raporu</h1>
          <p className="text-slate-400 text-sm mt-1">Detaylı kapanış ve hakediş özeti</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="bg-slate-900/60 border-slate-700 w-32" data-testid="report-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="bg-slate-900/60 border-slate-700 w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          {canAct(user, "export") && (<><Button onClick={() => download("excel")} className="bg-emerald-600 hover:bg-emerald-500" data-testid="export-excel">
            <FileSpreadsheet size={16} className="mr-1" /> Excel
          </Button>
          <Button onClick={() => download("pdf")} className="bg-red-600 hover:bg-red-500" data-testid="export-pdf">
            <FileText size={16} className="mr-1" /> PDF
          </Button></>)}
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-3 animate-fade-up delay-100">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 glass p-5">
          <p className="text-xs uppercase font-mono text-slate-500">Toplam İşlem</p>
          <p className="font-display text-3xl font-bold mt-1">{fmtNum(report.totals.count)}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 glass p-5">
          <p className="text-xs uppercase font-mono text-slate-500">Toplam Adet</p>
          <p className="font-display text-3xl font-bold text-emerald-300 mt-1">{fmtNum(report.totals.qty)}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 glass p-5">
          <p className="text-xs uppercase font-mono text-slate-500">Toplam Puan</p>
          <p className="font-display text-3xl font-bold text-amber-300 mt-1">{fmtNum(report.totals.points)}</p>
        </div>
      </div>

      <TargetProgressCard tp={report.target_progress} role={user?.role} />

      <div className="grid gap-6 lg:grid-cols-2 animate-fade-up delay-200">
        <Card className="border-slate-800 bg-slate-900/60 glass">
          <CardHeader><CardTitle className="text-base">Şube Bazlı</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead>Şube</TableHead>
                  <TableHead className="text-right">Adet</TableHead>
                  <TableHead className="text-right">Puan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.by_branch.map((b) => (
                  <TableRow key={b.branch_id} className="border-slate-800">
                    <TableCell><div className="font-semibold">{b.branch_name}</div><div className="text-xs font-mono text-slate-500">{b.branch_code}</div></TableCell>
                    <TableCell className="text-right">{fmtNum(b.qty)}</TableCell>
                    <TableCell className="text-right font-bold text-amber-300">{fmtNum(b.points)}</TableCell>
                  </TableRow>
                ))}
                {report.by_branch.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-6">Veri yok</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900/60 glass">
          <CardHeader><CardTitle className="text-base">Personel Bazlı</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead>Personel</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                  <TableHead className="text-right">Puan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.by_employee.sort((a,b) => b.points - a.points).map((e) => (
                  <TableRow key={e.user_id} className="border-slate-800">
                    <TableCell className="font-semibold">{e.name}</TableCell>
                    <TableCell className="text-right">{fmtNum(e.count)}</TableCell>
                    <TableCell className="text-right font-bold text-amber-300">{fmtNum(e.points)}</TableCell>
                  </TableRow>
                ))}
                {report.by_employee.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-6">Veri yok</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-300">
        <CardHeader><CardTitle className="text-base">Ürün Bazlı</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead>Ürün</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="text-right">Adet</TableHead>
                <TableHead className="text-right">Puan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.by_product.sort((a,b) => b.points - a.points).map((p) => (
                <TableRow key={p.product_id} className="border-slate-800">
                  <TableCell className="font-semibold">{p.name}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{p.sku}</TableCell>
                  <TableCell className="text-right">{fmtNum(p.qty)}</TableCell>
                  <TableCell className="text-right font-bold text-amber-300">{fmtNum(p.points)}</TableCell>
                </TableRow>
              ))}
              {report.by_product.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">Veri yok</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {report.campaigns.length > 0 && (
        <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-300">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Megaphone size={16} className="text-amber-400"/> Kampanya Hakedişleri</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {report.campaigns.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-xs text-slate-400 flex flex-wrap gap-1 items-center">
                      <Badge className="bg-slate-800 text-slate-300 border-slate-700">{c.scope === "branch" ? "Şube" : "Personel"}</Badge>
                      {c.tiers && c.tiers.length > 0 ? (
                        c.tiers.map((t, i) => (
                          <Badge key={i} className="bg-blue-500/10 text-blue-300 border-blue-500/30">
                            {t.threshold_qty}+ → {fmtNum(t.reward_amount)}₺
                          </Badge>
                        ))
                      ) : (
                        <span>Eşik: {c.threshold_qty}, Ödül: {fmtNum(c.reward_amount)}₺ ({c.reward_type === "per_unit" ? "adet başı" : "sabit"})</span>
                      )}
                    </div>
                  </div>
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">Toplam: {fmtNum(c.total_reward)}₺</Badge>
                </div>
                {c.qualifiers.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">Henüz hak eden yok.</p>
                ) : (
                  <div className="grid gap-1 mt-2 sm:grid-cols-2">
                    {c.qualifiers.map((q) => (
                      <div key={q.scope_id} className="flex justify-between items-center text-xs rounded-lg bg-slate-800/50 px-3 py-2">
                        <span className="font-medium">{q.label}</span>
                        <span>
                          <span className="text-slate-400">{fmtNum(q.qty)} adet</span>
                          {q.tier_threshold != null && <span className="text-blue-300 ml-1">(≥{q.tier_threshold})</span>}
                          <span className="text-amber-300 font-bold ml-1">· {fmtNum(q.reward)}₺</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
